import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { Button } from '../ui/button'

export const NETWORK_SEGMENTS_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/network-segments'
const NETWORK_ASSEMBLY_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/network-assembly'
const NETWORK_PROMOTE_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/network-assembly/promote'

/**
 * **Part of** for a floor network asset (spec 2026-10-20-35): the building or
 * site network of the same network it is a segment of, or none, changed in
 * one action, and **Move to building level**, which creates the building
 * network when missing and attaches every floor segment of the building.
 * Renders nothing for a network that is not filed under a floor.
 */
export function NetworkAssemblyControl(props: {
    org: string
    assetUid: string
    selectTestId: string
    promoteTestId: string
    /** The name of the building network when Move to building level creates it. */
    promoteName?: string
    onChanged?: () => void
}) {
    const { t } = useTranslation('assets')
    const queryClient = useQueryClient()
    const path = { orgCode: props.org, assetUid: props.assetUid }
    const { data } = $api.useQuery('get', NETWORK_SEGMENTS_PATH, { params: { path } })
    const attach = $api.useMutation('put', NETWORK_ASSEMBLY_PATH)
    const detach = $api.useMutation('delete', NETWORK_ASSEMBLY_PATH)
    const promote = $api.useMutation('post', NETWORK_PROMOTE_PATH)
    const [failed, setFailed] = useState(false)
    if (!data || data.network.level !== 'floor') return null

    const busy = attach.isPending || detach.isPending || promote.isPending
    const options = [...data.candidates]
    if (data.assembly && !options.some((c) => c.uid === data.assembly?.uid)) options.push(data.assembly)
    const done = async () => {
        setFailed(false)
        await queryClient.invalidateQueries({ queryKey: ['get', NETWORK_SEGMENTS_PATH] })
        props.onChanged?.()
    }
    const onSelect = async (uid: string) => {
        try {
            if (uid) await attach.mutateAsync({ params: { path }, body: { assembly_uid: uid } })
            else await detach.mutateAsync({ params: { path } })
            await done()
        } catch {
            setFailed(true)
        }
    }
    const onPromote = async () => {
        try {
            await promote.mutateAsync({ params: { path }, body: props.promoteName ? { name: props.promoteName } : {} })
            await done()
        } catch {
            setFailed(true)
        }
    }

    return (
        <div className="space-y-1">
            <div className="flex items-center gap-2">
                <span className="w-24 shrink-0 text-xs text-muted-foreground">{t('network_segments.part_of')}</span>
                <select
                    data-testid={props.selectTestId}
                    aria-label={t('network_segments.part_of')}
                    className="h-7 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground"
                    value={data.assembly?.uid ?? ''}
                    disabled={busy}
                    onChange={(e) => { void onSelect(e.target.value) }}
                >
                    <option value="">{t('network_segments.none')}</option>
                    {options.map((c) => (
                        <option key={c.uid} value={c.uid}>{c.name || c.uid}</option>
                    ))}
                </select>
            </div>
            {!data.assembly && data.candidates.length > 1 && (
                <p className="text-[11px] text-warning" data-testid={`${props.selectTestId}-choose`}>
                    {t('network_segments.choose')}
                </p>
            )}
            {!data.assembly && (
                <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    disabled={busy}
                    data-testid={props.promoteTestId}
                    onClick={() => { void onPromote() }}
                >
                    {t('network_segments.move_to_building')}
                </Button>
            )}
            {failed && (
                <p className="text-[11px] text-destructive" data-testid={`${props.selectTestId}-error`}>{t('network_segments.error')}</p>
            )}
        </div>
    )
}
